import { useUpdateMentorCapacity, useUpdateMentorData } from '@actions';
import ProfileIconPrimary from '@assets/icons/profile-icon-primary.png';
import ProfileIcon from '@assets/icons/profile-icon-white.png';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import Select from '@components/V4/Select/Select';
import { useAuthState } from '@contexts/AuthContext';
import { CoursesCanTeach, MentorWithRate } from '@shared/clientTypes';
import { MentorQualityScoreDTO } from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import { MENTOR_STATUS_V3 } from '@shared/constants';
import { UserRole } from '@shared/enums';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import './mentors-table-row.styles.scss';

type Props = {
  key?: string | number;
  mentorData: MentorWithRate;
  qualityScore?: MentorQualityScoreDTO;
  refetchSearchMentors: () => void;
  refetchMentorStatistics: () => void;
  onProfileModalOpen: () => void;
};

const MentorsTableRow = ({
  key,
  mentorData,
  qualityScore,
  refetchSearchMentors,
  refetchMentorStatistics,
  onProfileModalOpen,
}: Props) => {
  const { roles } = useAuthState();
  const isBDR = roles?.includes(UserRole.BDR);
  const { mutate: updateMentorCapacity } = useUpdateMentorCapacity(mentorData.mentor._id, {
    onSuccess: () => {
      refetchMentorStatistics();
    },
  });

  const [maxCap, setMaxCap] = useState(
    Number(mentorData.mentor.mentorData.mentorCapacity?.capacity)
  );

  const [universities, majors, degrees] = useMemo(() => {
    const majors: Set<string> = new Set();
    const universities: Set<string> = new Set();
    const degrees: Set<string> = new Set();

    mentorData?.mentor.educationalBackground?.forEach((background) => {
      const trimmedMajor = background.major?.trim();
      const trimmedUniversityInstitution = background.university?.trim();

      if (trimmedMajor) {
        majors.add(trimmedMajor);
      }

      if (trimmedUniversityInstitution) {
        universities.add(trimmedUniversityInstitution);
      }

      if (background.degreeType) {
        const trimmedDegree = background.degreeType.trim();
        if (trimmedDegree) {
          degrees.add(trimmedDegree);
        }
      }
    });

    mentorData.mentor.mentorData.universities?.forEach((university) => {
      const cleanUniversities = university
        .split(',')
        .map((splittedUniversity) => splittedUniversity.trim());

      cleanUniversities.forEach((cleanUniversity) => {
        if (cleanUniversity) {
          universities.add(cleanUniversity);
        }
      });
    });

    return [[...universities], [...majors], [...degrees]];
  }, [mentorData?.mentor.educationalBackground, mentorData.mentor.mentorData.universities]);

  const maxCapacityValue = mentorData.mentor.mentorData.mentorCapacity?.capacity;
  const capacityGap =
    maxCapacityValue !== undefined ? maxCapacityValue - (mentorData?.currentCapacity ?? 0) : null;

  const { mutate: updateMentorData } = useUpdateMentorData(mentorData.mentor._id, {
    onSuccess: () => {
      refetchSearchMentors();
    },
  });

  const courses: CoursesCanTeach[] = mentorData.mentor.mentorData.coursesCanTeach ?? [];

  return (
    <tr>
      {/*FIRST NAME*/}
      <td className="mentors-table-row-text">
        <span>{mentorData.mentor.firstName ?? '-'}</span>
      </td>

      {/*LAST NAME*/}
      <td className="mentors-table-row-text">
        <span>{mentorData.mentor.lastName ?? '-'}</span>
      </td>

      {/*EMAIL*/}
      {isBDR ? (
        <td className="mentors-table-row-text min-w-44">
          <span>{mentorData.mentor.email ?? '-'}</span>
        </td>
      ) : null}

      {/*STATUS*/}
      <td style={{}} className="mentors-table-row-text min-w-44">
        {isBDR ? (
          <span>{mentorData.mentor.mentorData.status ?? 'N/A'}</span>
        ) : (
          <Select
            onSelect={(value) => {
              updateMentorData({
                status: value as string | undefined,
              });
            }}
            value={mentorData.mentor.mentorData.status ?? 'N/A'}
            options={MENTOR_STATUS_V3.map((option) => ({
              value: option,
              label: option,
            }))}
            allowClear={false}
            variant="ghost"
          />
        )}
      </td>

      {/*CAPACITY*/}
      <td className="mentors-table-row-text">
        <span>{mentorData?.currentCapacity ?? '-'}</span>
      </td>

      {/*MAX CAPACITY*/}
      <td style={{}} className="mentors-table-row-text">
        {isBDR ? (
          <span>{maxCap || 0}</span>
        ) : (
          <span>
            <input
              className="block w-full rounded-md border-0 py-1.5 px-0 text-white shadow-sm ring-0 ring-inset ring-gray-300 placeholder:text-gray-400 focus:px-1.5 focus:ring-1 focus:ring-inset focus:ring-white hover:ring-1 hover:ring-inset hover:ring-white sm:text-sm sm:leading-6"
              style={{
                backgroundColor: 'transparent',
                width: '64px',
                height: '26px',
                marginBottom: '0px',
                fontSize: '12px',
                lineHeight: '16px',
              }}
              type="number"
              value={maxCap}
              min={0}
              onChange={(e) => setMaxCap(Number(e.target.value))}
              onBlur={() => {
                updateMentorCapacity({
                  capacity: maxCap,
                });
              }}
            />
            <span className="h-0 w-0 block absolute text-0">{maxCap}</span>
          </span>
        )}
      </td>

      {/*MAX CAPACITY UPDATED AT*/}
      <td style={{}} className="mentors-table-row-text">
        {isBDR ? (
          <span>
            {moment(mentorData.mentor.mentorData.mentorCapacity?.updatedAt).format('DD.MM.YYYY')}
          </span>
        ) : (
          <span>
            <input
              className="block w-full rounded-md border-0 py-1.5 px-0 text-white shadow-sm ring-0 ring-inset ring-gray-300 placeholder:text-gray-400 focus:px-1.5 focus:ring-1 focus:ring-inset focus:ring-white hover:ring-1 hover:ring-inset hover:ring-white sm:text-sm sm:leading-6"
              style={{
                backgroundColor: 'transparent',
                width: '88px',
                height: '26px',
                marginBottom: '0px',
                fontSize: '12px',
                lineHeight: '16px',
              }}
              type="text"
              value={
                mentorData.mentor.mentorData.mentorCapacity?.updatedAt
                  ? moment(mentorData.mentor.mentorData.mentorCapacity?.updatedAt).format(
                      'DD.MM.YYYY'
                    )
                  : '-'
              }
            />
            <span className="h-0 w-0 block absolute text-0">
              {mentorData.mentor.mentorData.mentorCapacity?.updatedAt
                ? moment(mentorData.mentor.mentorData.mentorCapacity?.updatedAt).format(
                    'DD.MM.YYYY'
                  )
                : '-'}
            </span>
          </span>
        )}
      </td>

      {/*CAPACITY GAP*/}
      <td className="mentors-table-row-text">
        <span>{capacityGap !== null ? capacityGap : '-'}</span>
      </td>

      {/*AI SCORE*/}
      {!isBDR && (
        <td className="mentors-table-row-text">
          <QualityScoreBadge
            score={qualityScore?.average}
            suffix={
              qualityScore ? (
                <span className="font-normal opacity-70">({qualityScore.graded})</span>
              ) : null
            }
          />
        </td>
      )}

      {/*PAY RATE*/}
      {!isBDR && (
        <td className="mentors-table-row-text">
          <span>
            {mentorData?.rate?.amount !== undefined && mentorData?.rate?.currency !== undefined
              ? `${mentorData?.rate?.amount} ${mentorData?.rate?.currency}/h`
              : '-'}
          </span>
        </td>
      )}

      {/*UNIVERSITIES*/}
      <td style={{}} className="mentors-table-row-text">
        {/*TOOLTIP*/}
        {universities.length > 1 ? (
          <Tooltip
            zIndex={1}
            color="#333333"
            title={
              <div>
                {universities.length > 1 &&
                  universities?.map((university: string) => (
                    <>
                      <span>{university};</span>
                      <br />
                    </>
                  ))}
              </div>
            }
          >
            <span>
              {Array.isArray(universities) && universities.length > 1
                ? universities[0] + '...'
                : Array.isArray(universities) && universities.length > 0
                ? universities[0] ?? '-'
                : '-'}
            </span>
          </Tooltip>
        ) : (
          <span>
            {Array.isArray(universities) && universities.length > 1
              ? universities[0] + '...'
              : Array.isArray(universities) && universities.length > 0
              ? universities[0] ?? '-'
              : '-'}
          </span>
        )}
      </td>

      {/*COURSES*/}
      <td className="mentors-table-row-text">
        {/*TOOLTIP*/}
        {courses.length > 1 ? (
          <Tooltip
            zIndex={1}
            color="#333333"
            title={
              <div>
                {courses.length > 1 &&
                  courses?.map((course: any) => (
                    <>
                      <span>{course?.title};</span>
                      <br />
                    </>
                  ))}
              </div>
            }
          >
            <span>
              {Array.isArray(courses) && courses.length > 1
                ? courses?.[0]?.title + '...'
                : Array.isArray(courses) && courses.length > 0
                ? courses?.[0]?.title ?? '-'
                : '-'}
            </span>
          </Tooltip>
        ) : (
          <span>
            {Array.isArray(courses) && courses.length > 1
              ? courses?.[0]?.title + '...'
              : Array.isArray(courses) && courses.length > 0
              ? courses?.[0]?.title ?? '-'
              : '-'}
          </span>
        )}
      </td>

      {/*STUDY SUBJECTS*/}
      <td className="mentors-table-row-text">
        <span>{majors?.join(', ')}</span>
      </td>

      {/*DEGREES*/}
      <td className="mentors-table-row-text">
        <span>{degrees?.join(', ')}</span>
      </td>

      {/*CREATED AT*/}
      <td className="mentors-table-row-text">
        <span>{moment(mentorData?.mentor?.createdAt).format('DD.MM.YYYY')}</span>
      </td>

      {/*ACCESS*/}

      {!isBDR && (
        <td>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '25%', display: 'flex' }}>
              <div
                onClick={onProfileModalOpen}
                style={{
                  display: 'flex',
                  marginRight: '12px',
                  cursor: 'pointer',
                }}
                className="enrollments-action"
              >
                <img
                  style={{
                    marginRight: '8px',
                    minWidth: '14px',
                    maxWidth: '14px',
                  }}
                  className="external-link-icon"
                  src={ProfileIcon}
                  alt="enrollments"
                />
                <img
                  style={{
                    marginRight: '8px',
                    minWidth: '14px',
                    maxWidth: '14px',
                  }}
                  className="external-link-icon-primary"
                  src={ProfileIconPrimary}
                  alt="enrollments"
                />
                <span className="mentors-table-access-button">Profile</span>
              </div>
            </div>
          </div>
        </td>
      )}
    </tr>
  );
};

export default MentorsTableRow;
